import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getStoredUser,
  getAuthToken,
  sendCustomerMobileOtp,
  verifyCustomerMobileOtp,
  googleLogin,
  initGuestSession,
  updateUserProfile,
  logout as apiLogout,
  AUTH_CHANGE_EVENT,
} from '../api/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [token, setToken] = useState(() => getAuthToken());
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [modalRedirect, setModalRedirect] = useState(null);
  const [modalSuccessCallback, setModalSuccessCallback] = useState(null);

  const refreshSession = useCallback(() => {
    const storedUser = getStoredUser();
    const storedToken = getAuthToken();
    setUser(storedUser);
    setToken(storedToken);
  }, []);

  useEffect(() => {
    const handleAuthEvent = () => refreshSession();
    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthEvent);
    window.addEventListener('storage', handleAuthEvent);
    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthEvent);
      window.removeEventListener('storage', handleAuthEvent);
    };
  }, [refreshSession]);

  const openAuthModal = useCallback((options = {}) => {
    if (options.redirect) setModalRedirect(options.redirect);
    if (options.onSuccess) setModalSuccessCallback(() => options.onSuccess);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
    setModalRedirect(null);
    setModalSuccessCallback(null);
  }, []);

  const handleLoginSuccess = useCallback((resultUser, resultToken) => {
    setUser(resultUser);
    setToken(resultToken || null);
    if (modalSuccessCallback) {
      modalSuccessCallback(resultUser, resultToken);
    }
    closeAuthModal();
  }, [modalSuccessCallback, closeAuthModal]);

  const sendOtp = async (mobile) => {
    return await sendCustomerMobileOtp(mobile);
  };

  const verifyOtp = async (mobile, otp) => {
    const result = await verifyCustomerMobileOtp(mobile, otp);
    if (result?.success && result?.user) {
      setUser(result.user);
      setToken(result.access_token || null);
    }
    return result;
  };

  const loginWithGoogle = async (idTokenOrEmail) => {
    const result = await googleLogin(idTokenOrEmail);
    if (result?.success && result?.user) {
      handleLoginSuccess(result.user, result.access_token);
    }
    return result;
  };

  const loginAsGuest = async () => {
    const guestUser = await initGuestSession();
    handleLoginSuccess(guestUser, guestUser.token);
    return guestUser;
  };

  const updateProfile = async (data) => {
    const updated = await updateUserProfile(data);
    setUser(updated);
    return updated;
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
    setToken(null);
  };

  const isAuthenticated = Boolean(user && user.token && !user.is_guest);
  const isGuest = Boolean(user?.is_guest);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isGuest,
        isAuthModalOpen,
        modalRedirect,
        openAuthModal,
        closeAuthModal,
        sendOtp,
        verifyOtp,
        loginWithGoogle,
        loginAsGuest,
        updateProfile,
        logout,
        handleLoginSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
